/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";

export const GetZapParams = z.object({
  zap_id: z
    .string()
    .describe(
      "The unique identifier for the Zap you want to retrieve details for",
    ),
  account_id: z
    .string()
    .describe(
      "The account ID that owns the Zap. This is required to fetch the Zap.",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Getting Zap details...' or 'Loading workflow...'",
    ),
});

export type GetZapParamsType = z.infer<typeof GetZapParams>;

export const getZapExecute = async (rawArgs: any) => {
  let params: GetZapParamsType;
  try {
    params = GetZapParams.parse(rawArgs || {});
  } catch (error) {
    console.error("Error parsing arguments for getZap:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for get_zap. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { zap_id, account_id } = params;

  const query = new URLSearchParams({
    zap_id,
    account_id,
  });
  const proxyApiUrl = `/api/canvasui/composer/getZap?${query.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for get_zap (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for get_zap (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for get_zap (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch {
        errorDetailString = `Proxy error for get_zap (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[GET_ZAP_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[GET_ZAP_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[GET_ZAP_TOOL] Error in getZapExecute:", error);
    return {
      $error: {
        message: `Error fetching Zap: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const getZapDescription = `Retrieves the detailed configuration of a specific Zap, including its overall settings and all its steps.
This information is crucial for understanding a Zap's current setup and is often a prerequisite for other Zap-related tools.

IMPORTANT:
- The output of this tool provides the full Zap Object Definition Language (ZDL) structure.
- This structure should be used as the basis for any modifications when calling 'update_zap'.
- Specific details from the steps (like 'app_id', 'action' (action key), 'type', 'params', 'authentication_id') are required inputs for tools like 'needs_api' and 'choices_api'.
- Pay close attention to the 'app_id' for each step, as this exact value (which might include a version like 'AppNameCLIAPI@1.2.3' or be a simple slug like 'gmail') is critical for other tools.

Required parameters:
- zap_id: The ID of the Zap to fetch.
    - If the user mentions a Zap by name on the canvas, try to find its ID in 'additionalData.canvas.nodes' (look for a node with a matching label and use its 'data.zapId' or 'data.zap_id' property).
    - If the Zap ID is directly provided or known, use that.
- account_id: The ID of the account that owns the Zap.
    - This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value.

Output:
- The tool returns a JSON object representing the full ZDL configuration of the Zap.
- Key information includes the Zap's 'title', 'timezone', 'steps' array, etc.
- Each object in the 'steps' array contains details like 'id', 'app' (short name), 'app_id' (potentially versioned, critical for other tools), 'type', 'action' (action key), 'params' (current settings), and 'authentication_id' (if any).`;

export const getZapAtom = atom<JotaiTool<z.infer<typeof GetZapParams>>>(() => ({
  name: "get_zap",
  description: getZapDescription,
  parameters: GetZapParams,
  execute: atom(null, async (_get, _set, { args }) => {
    return getZapExecute(args);
  }),
}));
getZapAtom.debugLabel = "getZapAtom";
/* eslint-enable sort-exports/sort-exports */
